import { Injectable, inject, signal } from "@angular/core";
import { OneSignal } from "onesignal-ngx";
import { environment } from "./environment";
import { AuthService } from "./auth.service";

export type NotificationPermission = "default" | "granted" | "denied";

@Injectable({ providedIn: "root" })
export class NotificationService {
	private readonly auth = inject(AuthService);
	private readonly oneSignal = new OneSignal();

	readonly permission = signal<NotificationPermission>(
		typeof Notification !== "undefined"
			? (Notification.permission as NotificationPermission)
			: "default",
	);
	readonly isSubscribed = signal(false);

	private initialized = false;

	async initialize(): Promise<void> {
		if (this.initialized || typeof window === "undefined") return;
		this.initialized = true;

		// The onesignal-ngx SDK uses ServiceWorker APIs that don't exist in iOS
		// WKWebView under the capacitor:// scheme; its init() hangs indefinitely,
		// which blocks APP_INITIALIZER and leaves <app-root> empty. Native push
		// will be wired up through the OneSignal Capacitor plugin separately.
		const isNative =
			(
				window as unknown as {
					Capacitor?: { isNativePlatform?: () => boolean };
				}
			).Capacitor?.isNativePlatform?.() === true;
		if (isNative) return;

		// Skip if the appId is still the placeholder — avoids a hang against a
		// non-existent OneSignal project in local dev.
		if (
			!environment.oneSignalAppId ||
			environment.oneSignalAppId.startsWith("REPLACE_")
		) {
			return;
		}

		try {
			await this.oneSignal.init({
				appId: environment.oneSignalAppId,
				promptOptions: {
					slidedown: {
						prompts: [{ type: "push", autoPrompt: false, delay: {} }],
					},
				},
			});
		} catch (err) {
			console.warn(
				"OneSignal init failed — push notifications unavailable:",
				err,
			);
			return;
		}

		this.permission.set(
			typeof Notification !== "undefined"
				? (Notification.permission as NotificationPermission)
				: "default",
		);

		const user = this.auth.currentUser();
		if (user) {
			try {
				await this.oneSignal.login(user.id);
			} catch (err) {
				console.warn("OneSignal login failed:", err);
			}
		}

		try {
			const optedIn = this.oneSignal.User.PushSubscription.optedIn;
			this.isSubscribed.set(optedIn ?? false);
		} catch {
			/* ignore */
		}
	}

	/**
	 * Tag the current OneSignal subscription with the session they're in so we
	 * can segment audiences in the dashboard (e.g. nudge everyone in a team).
	 * Safe to call before initialize(): the SDK queues tag writes.
	 */
	tagActiveSession(sessionId: string, teamName: string): void {
		try {
			this.oneSignal.User.addTags({
				session_id: sessionId,
				team_name: teamName,
			});
		} catch (err) {
			console.warn("OneSignal addTags failed:", err);
		}
	}

	/** Drop the session tags once the game ends or the user leaves. */
	clearSessionTags(): void {
		try {
			this.oneSignal.User.removeTags(["session_id", "team_name"]);
		} catch (err) {
			console.warn("OneSignal removeTags failed:", err);
		}
	}

	async requestPermission(): Promise<boolean> {
		if (typeof Notification === "undefined") return false;
		if (Notification.permission === "denied") {
			this.permission.set("denied");
			return false;
		}
		if (Notification.permission === "granted") {
			await this.oneSignal.User.PushSubscription.optIn();
			this.isSubscribed.set(true);
			this.permission.set("granted");
			return true;
		}

		const result = await Notification.requestPermission();
		this.permission.set(result as NotificationPermission);

		if (result === "granted") {
			await this.oneSignal.User.PushSubscription.optIn();
			this.isSubscribed.set(true);
			return true;
		}
		return false;
	}

	scheduleLocal(
		title: string,
		body: string,
		delayMs: number,
	): ReturnType<typeof setTimeout> {
		return setTimeout(async () => {
			if (typeof Notification === "undefined") return;
			if (Notification.permission !== "granted") return;

			if ("serviceWorker" in navigator) {
				const registration = await navigator.serviceWorker.ready.catch(
					() => null,
				);
				if (registration) {
					await registration.showNotification(title, {
						body,
						icon: "/icons/icon-192.png",
					});
					return;
				}
			}

			new Notification(title, { body, icon: "/icons/icon-192.png" });
		}, delayMs);
	}
}
